import { FilterOperators } from '@kirin/api-client/contracts/table';
import {
  MCP_FILTERABLE_FIELDS,
  MCP_RISK_LEVELS,
  mcpRiskLevelLabels,
  statusLabels,
  type MCPFilterableField,
} from '@kirin/api-client/contracts/mcp';

/**
 * URL key map for MCP filter fields.
 * The `satisfies` constraint enforces exhaustiveness — TypeScript will error
 * if a new field is added to MCP_FILTERABLE_FIELDS without a URL key here.
 */
export const MCP_FILTER_URL_KEYS = {
  [MCP_FILTERABLE_FIELDS.NAME]: 'n',
  [MCP_FILTERABLE_FIELDS.VERSION]: 'v',
  [MCP_FILTERABLE_FIELDS.PUBLISHER]: 'pub',
  [MCP_FILTERABLE_FIELDS.TYPE]: 'ty',
  [MCP_FILTERABLE_FIELDS.TRANSPORT]: 'tr',
  [MCP_FILTERABLE_FIELDS.USERS]: 'u',
  [MCP_FILTERABLE_FIELDS.TOOLS]: 'to',
  [MCP_FILTERABLE_FIELDS.STATUS]: 'st',
  [MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT]: 'fs',
  [MCP_FILTERABLE_FIELDS.LAST_SEEN_AT]: 'ls',
  // KIR-3940: never registered as a DataTable ColumnConfig — the source-filter
  // buttons own this filter via a bare `pr` URL param (see sourceFilter.ts).
  // This entry exists solely to satisfy the exhaustiveness check above.
  [MCP_FILTERABLE_FIELDS.PROVENANCE]: 'pr',
  // KIR-5108 AgentMesh risk column (registered only while the flag is on).
  [MCP_FILTERABLE_FIELDS.RISK]: 'rk',
} as const satisfies Record<MCPFilterableField, string>;

export const MCP_TABLE_CONFIG = {
  /** DataTable id — must match the id prop on the DataTable component */
  tableId: 'inv-mcp',
  defaultOperator: FilterOperators.INCLUDES,
} as const;

export const MCP_TYPE_OPTIONS = [
  { value: 'local', label: 'Local' },
  { value: 'remote', label: 'Remote' },
];

export const MCP_TRANSPORT_OPTIONS = [
  { value: 'http', label: 'HTTP' },
  { value: 'stdio_local', label: 'command + local files' },
  { value: 'stdio_repo', label: 'command + repository' },
  { value: 'stdio_package', label: 'command + package' },
];

// Derived from the shared statusLabels contract so search + filter copy stay in sync.
export const MCP_STATUS_OPTIONS = Object.entries(statusLabels).map(
  ([value, label]) => ({ value, label })
);

// KIR-5108: the five AgentMesh risk levels, in AgentMesh's own order.
export const MCP_RISK_OPTIONS = MCP_RISK_LEVELS.map(value => ({
  value,
  label: mcpRiskLevelLabels[value],
}));
